import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
    ClientDto,
    GoodsDto,
    OrderDto,
    OrderStatus,
    PurchaseOrderDto,
    SpecializedOrderStatus,
    SiteDto,
} from '../../api/ClientApi';
import { Box, Chip, Collapse, IconButton, Paper, TablePagination, Tooltip, Typography } from '@mui/material';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/reducers';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { importAllGoods } from '../../redux/actions/goodsActions';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { importApprovedOrderGoodsItems } from '../../redux/actions/orderGoodsItemsActions';
import PurchaseOrdersTableMenu from './PurchaseOrdersTableMenu';
import { togglePurchaseOrderIdCollapsible } from '../../redux/actions/collapsibleOpenStateActions';
import { isPurchaseOrderCollapsed } from '../../redux/selectors/collapsibleSelector';
import { CompareTwoDateTimes } from '../../common/datesHelper';
import PurchaseOrderForm from './form/PurchaseOrderForm';
import { getClientNameInfo, getSiteOrdersTableData } from '../orders/OrdersHelper';
import PurchaseOrderChangeStatusAlert from './form/PurchaseOrderChangeStatusAlert';
import { SpecializedOrderStatusSR } from '../../common/Types';
import { getColorForSpecializedOrderStatus } from './PurchaseOrdersHelper';
import PurchaseOrderToReceptionOrderForm from './form/PurchaseOrderToReceptionOrderForm';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import OrdersTable from '../orders/OrdersTable';

import { MobileCardRow, mobileHiddenHeadSx, useIsMobile } from '../../common/components/mobile/MobilePrimitives';

function createData(
    id?: number,
    destinationSite?: SiteDto,
    documentCode?: string,
    goods?: GoodsDto[],
    receptionOrders?: OrderDto[],
    created?: Date,
    purchaseOrderStatus?: SpecializedOrderStatus,
    sites?: SiteDto[],
    clients?: ClientDto[],
) {
    const destinationData = !destinationSite
        ? '/'
        : getSiteOrdersTableData(destinationSite, sites ?? undefined, clients ?? undefined);

    const originData = !goods || !goods[0]?.batch?.supplier ? '/' : getClientNameInfo(goods[0]?.batch?.supplier);

    const art = !!goods
        ? goods
              ?.filter((g) => !!g.article?.title && !!g.quantity)
              .map((g) => {
                  const deliveredGoodsItems = receptionOrders
                      ?.filter((ro) => ro.orderStatus === OrderStatus.Delivered)
                      ?.map((ro) => ro.orderGoodsItems ?? [])
                      .flat();

                  const deliveredGoodsForArticle = deliveredGoodsItems?.filter(
                      (ogi) => ogi.articleId === g.article?.id,
                  );
                  const deliveredQuantity = deliveredGoodsForArticle?.reduce((acc, val) => {
                      return (acc += val.quantity ?? 0);
                  }, 0);

                  return { article: g.article, quantity: g.quantity ?? 0, deliveredQuantity: deliveredQuantity };
              })
        : [];

    return {
        id,
        documentCode,
        originData,
        destinationData,
        art,
        created,
        purchaseOrderStatus,
        receptionOrders,
    };
}

type PurchaseOrderRow = ReturnType<typeof createData>;

type PurchaseOrderRowProps = {
    row: PurchaseOrderRow;
    extendedDetails: boolean;
    onOpenPurchaseOrderForm: (orderId?: number) => void;
    onCancelPurchaseOrder: (orderId?: number) => void;
    onClosePurchaseOrder: (orderId?: number) => void;
    onCreateReceptionOrder: (orderId?: number) => void;
};

/**
 * One purchase order row: desktop table row + collapsible details, or a mobile card.
 * Lives at module scope so React updates rows in place instead of remounting them on every parent render.
 */
function Row({
    row,
    extendedDetails,
    onOpenPurchaseOrderForm,
    onCancelPurchaseOrder,
    onClosePurchaseOrder,
    onCreateReceptionOrder,
}: PurchaseOrderRowProps) {
    const dispatch = useAppDispatch();
    const isCollapsed = useAppSelector(isPurchaseOrderCollapsed(row.id));

    const handleToggleOrderCollapsible = () => {
        dispatch(togglePurchaseOrderIdCollapsible(row.id));
    };

    const isMobile = useIsMobile();

    const rowMenu = (
        <PurchaseOrdersTableMenu
            goToPurchaseOrderForm={() => onOpenPurchaseOrderForm(row.id)}
            goToCancelPurchaseOrderForm={() => onCancelPurchaseOrder(row.id)}
            goToClosePurchaseOrderForm={() => onClosePurchaseOrder(row.id)}
            goToReceptionOrderForm={() => onCreateReceptionOrder(row.id)}
            orderStatus={row.purchaseOrderStatus}
        />
    );

    const detailsContent = (
        <>
            <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                <Box sx={{ margin: 1 }}>
                    {extendedDetails ? (
                        <OrdersTable
                            disableActions
                            orders={row.receptionOrders ?? null}
                            demandOrders={null}
                            returnOrders={null}
                        />
                    ) : (
                        <Table size="small" aria-label="purchases">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Artikal</TableCell>
                                    <TableCell align="center">Količina</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {row.art?.map((article, index) => {
                                    return (
                                        <TableRow
                                            key={`${article.article?.id ?? 0}-${index}`}
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                            }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {article.article?.title
                                                    ? article.article.manufacturer
                                                        ? article.article.title +
                                                          ' (' +
                                                          article.article.manufacturer +
                                                          ')'
                                                        : article.article.title
                                                    : '/'}
                                            </TableCell>
                                            <TableCell align="center">
                                                <span style={{ opacity: 0.5 }}>
                                                    {(article.deliveredQuantity ?? 0).toString()}/
                                                </span>
                                                {(article.quantity ?? '/').toString()}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Collapse>
        </>
    );

    if (isMobile) {
        return (
            <>
                <MobileCardRow
                    title={row.documentCode ?? '/'}
                    highlighted={!isCollapsed}
                    header={
                        !!row?.purchaseOrderStatus ? (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={SpecializedOrderStatusSR[row.purchaseOrderStatus]}
                                color={getColorForSpecializedOrderStatus(row.purchaseOrderStatus)}
                            />
                        ) : undefined
                    }
                    actions={
                        <Box sx={{ display: 'flex' }}>
                            <IconButton aria-label="expand row" size="small" onClick={handleToggleOrderCollapsible}>
                                {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                            {rowMenu}
                        </Box>
                    }
                    fields={[
                        { label: 'Dobavljač', value: row.originData ?? '/' },
                        { label: 'Odredište', value: row.destinationData ?? '/' },
                        { label: 'Br.', value: row.id ?? '/' },
                    ]}
                />
                <TableRow>
                    <TableCell sx={{ p: 0, borderBottom: 'none' }} colSpan={99}>
                        {detailsContent}
                    </TableCell>
                </TableRow>
            </>
        );
    }

    return (
        <>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, bgcolor: !isCollapsed ? '#eee' : '' }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => handleToggleOrderCollapsible()}>
                        {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {row.id ?? '/'}
                </TableCell>
                <TableCell align="center" component="th" scope="row">
                    {row.documentCode ?? '/'}
                </TableCell>
                <TableCell align="center">{row.originData ?? '/'}</TableCell>
                <TableCell align="center">{row.destinationData ?? '/'}</TableCell>
                <TableCell align="center">
                    {!!row?.purchaseOrderStatus && (
                        <Chip
                            variant="outlined"
                            label={SpecializedOrderStatusSR[row?.purchaseOrderStatus]}
                            color={getColorForSpecializedOrderStatus(row?.purchaseOrderStatus)}
                        />
                    )}
                </TableCell>
                <TableCell align="center" sx={{ width: 50, py: 0 }}>
                    <div>{rowMenu}</div>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    {detailsContent}
                </TableCell>
            </TableRow>
        </>
    );
}

type PurchaseOrdersTableProps = {
    purchaseOrders: PurchaseOrderDto[] | null;
    extendedDetails: boolean;
};

export default function PurchaseOrdersTable(props: PurchaseOrdersTableProps) {
    const { purchaseOrders, extendedDetails } = props;

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [purchaseOrderId, setPurchaseOrderId] = React.useState<number | undefined>(undefined);
    const [cancelOrderPurchaseOrderId, setCancelOrderPurchaseOrderId] = React.useState<number | undefined>(undefined);
    const [closeOrderPurchaseOrderId, setCloseOrderPurchaseOrderId] = React.useState<number | undefined>(undefined);
    const [receptionOrderPurchaseOrderId, setReceptionOrderPurchaseOrderId] = React.useState<number | undefined>(
        undefined,
    );

    const goToPurchaseOrderForm = (orderId?: number) => {
        setPurchaseOrderId(orderId);
    };

    const handleClosePurchaseOrderForm = () => {
        setPurchaseOrderId(undefined);
    };

    const goToCancelPurchaseOrderForm = (orderId?: number) => {
        setCancelOrderPurchaseOrderId(orderId);
    };

    const handleCloseCancelPurchaseOrderForm = () => {
        setCancelOrderPurchaseOrderId(undefined);
        setCloseOrderPurchaseOrderId(undefined);
    };

    const goToClosePurchaseOrderForm = (orderId?: number) => {
        setCloseOrderPurchaseOrderId(orderId);
    };

    const goToReceptionOrderForm = (orderId?: number) => {
        setReceptionOrderPurchaseOrderId(orderId);
    };

    const handleCloseReceptionOrderForm = () => {
        setReceptionOrderPurchaseOrderId(undefined);
    };

    const goods = useSelector((state: RootState) => state.goods.goods);
    const approvedOrderGoodsItems = useSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);
    const sites = useSelector((state: RootState) => state.sites.sites);
    const clients = useSelector((state: RootState) => state.clients.clients);

    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (!goods) {
            dispatch(importAllGoods());
        }
    }, [dispatch]);

    React.useEffect(() => {
        if (!approvedOrderGoodsItems) {
            dispatch(importApprovedOrderGoodsItems());
        }
    }, [dispatch]);

    const orderRows = purchaseOrders?.map((order) => {
        const purchOrderStatus = order.purchaseOrderStatus;
        return createData(
            order.id,
            order.destinationSite,
            order.documentCode,
            order.goods,
            order.receptionOrders,
            order.created,
            purchOrderStatus,
        );
    });

    const rows = (orderRows ?? []).sort((a, b) => {
        const aConst =
            a.purchaseOrderStatus === SpecializedOrderStatus.Cancelled ||
            a.purchaseOrderStatus === SpecializedOrderStatus.Closed
                ? 0
                : 1;
        const bConst =
            b.purchaseOrderStatus === SpecializedOrderStatus.Cancelled ||
            b.purchaseOrderStatus === SpecializedOrderStatus.Closed
                ? 0
                : 1;

        if (aConst === bConst) {
            return CompareTwoDateTimes(b.created ?? new Date(), a.created ?? new Date(), false) ? -1 : 1;
        }
        return bConst - aConst;
    });

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (rows?.length ?? 0)) : 0;

    const visibleRows = React.useMemo(
        () => (!rows ? [] : [...rows].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
        [page, rowsPerPage, purchaseOrders, goods, approvedOrderGoodsItems, sites, clients],
    );

    React.useEffect(() => {
        if (!rows || rows?.length <= page * rowsPerPage) {
            setPage(0);
        }
    }, [rows, page]);

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <TableContainer
                        sx={{
                            minHeight: '100px',
                            margin: 'auto',
                        }}
                    >
                        <Table size="small" stickyHeader aria-label="simple table">
                            <TableHead sx={mobileHiddenHeadSx}>
                                <TableRow>
                                    <StyledTableCell />
                                    <StyledTableCell>Id</StyledTableCell>
                                    <Tooltip title="Br. računa/predračuna/fakture" placement="top">
                                        <StyledTableCell
                                            align="center"
                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            Br. dokumenta <InfoOutlineIcon sx={{ height: '20px', ml: '2px' }} />
                                        </StyledTableCell>
                                    </Tooltip>
                                    <StyledTableCell align="center">Dobavljač</StyledTableCell>
                                    <StyledTableCell align="center">Odredište</StyledTableCell>
                                    <StyledTableCell align="center">Status</StyledTableCell>
                                    <StyledTableCell align="center" sx={{ width: 100 }}>
                                        Akcije
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows?.map((row, index) => (
                                    <Row
                                        key={row.id ?? index}
                                        row={row}
                                        extendedDetails={extendedDetails}
                                        onOpenPurchaseOrderForm={goToPurchaseOrderForm}
                                        onCancelPurchaseOrder={goToCancelPurchaseOrderForm}
                                        onClosePurchaseOrder={goToClosePurchaseOrderForm}
                                        onCreateReceptionOrder={goToReceptionOrderForm}
                                    />
                                ))}
                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height: 53 * emptyRows,
                                        }}
                                    >
                                        <TableCell colSpan={8} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={rows?.length ?? 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
            <PurchaseOrderForm
                closeForm={handleClosePurchaseOrderForm}
                open={!!purchaseOrderId}
                purchaseOrderId={purchaseOrderId}
            />
            <PurchaseOrderChangeStatusAlert
                closeForm={handleCloseCancelPurchaseOrderForm}
                purchaseOrderId={cancelOrderPurchaseOrderId ?? closeOrderPurchaseOrderId ?? 0}
                open={!!cancelOrderPurchaseOrderId || !!closeOrderPurchaseOrderId}
                purchaseOrderStatus={
                    !!cancelOrderPurchaseOrderId
                        ? SpecializedOrderStatus.Cancelled
                        : !!closeOrderPurchaseOrderId
                          ? SpecializedOrderStatus.Closed
                          : SpecializedOrderStatus.Created
                }
            />
            <PurchaseOrderToReceptionOrderForm
                closeForm={handleCloseReceptionOrderForm}
                purchaseOrderId={receptionOrderPurchaseOrderId ?? 0}
                open={!!receptionOrderPurchaseOrderId}
            />
            {/* <CancelOrderForm
                closeForm={handleCloseCancelPurchaseOrderForm}
                open={!!cancelOrderPurchaseOrderId}
                orderId={cancelOrderPurchaseOrderId}
            /> */}
        </>
    );
}
